import { useReactiveVar } from '@apollo/client';
import { faCheck, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';

import FilterDropdown from '../FilterDropdown';
import FilterPill from '../FilterPill';
import { buildMultiChipLabel } from '../helpers';

import { useGetCustomerAndAccountsQuery } from '@/graphql';
import { CustomerOnboardingStatusEnum } from '@/types/graphql';
import { currentAgencyVar } from '@/util/apollo/cache';

type Props = {
  /** The customer admin's own customer id — accounts are scoped to it. */
  customerId: string;
  value: string[] | undefined;
  onApply: (next: string[] | undefined) => void;
  onClear: () => void;
};

const BillingAccountPill = ({ customerId, value, onApply, onClear }: Props) => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<string[]>(() => value || []);
  const [search, setSearch] = useState('');

  const { data } = useGetCustomerAndAccountsQuery({
    variables: {
      agencyId: currentAgency!.id,
      filters: {
        status: [1],
        onboardingStatus: [CustomerOnboardingStatusEnum.COMPLETED],
      },
    },
  });

  const accounts = useMemo(() => {
    const customer = data?.agency.customers.items.find(
      (c) => c.id === customerId,
    );
    return (customer?.accounts || [])
      .map((a) => ({ id: a.id, name: a.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data, customerId]);

  const filteredAccounts = useMemo(() => {
    if (!search) return accounts;
    const q = search.toLowerCase();
    return accounts.filter((a) => a.name.toLowerCase().includes(q));
  }, [accounts, search]);

  useEffect(() => {
    if (open) {
      setStaged(value || []);
      setSearch('');
    }
  }, [open, value]);

  const toggleItem = (id: string) => {
    setStaged((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const apply = () => {
    onApply(staged.length > 0 ? staged : undefined);
    setOpen(false);
  };

  const selectedNames = (value || [])
    .map((id) => accounts.find((a) => a.id === id)?.name)
    .filter((n): n is string => Boolean(n));
  const isActive = selectedNames.length > 0;
  const { label, more } = buildMultiChipLabel(selectedNames);

  return (
    <div className="pill-wrap" ref={wrapRef}>
      <FilterPill
        placeholder="Billing"
        chipLabel={isActive ? label : undefined}
        chipMore={isActive ? more : undefined}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onClear={isActive ? onClear : undefined}
        a11yLabel="Filter by billing account"
      />
      <FilterDropdown
        open={open}
        onClose={() => setOpen(false)}
        onApply={apply}
        anchorRef={wrapRef}
        panelStyle={{ minWidth: 260 }}
      >
        <div className="dropdown-search">
          <FontAwesomeIcon icon={faSearch} color="#979797" />
          <input
            type="text"
            placeholder="Search billing account"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="dropdown-scroll">
          {filteredAccounts.map((a) => {
            const checked = staged.includes(a.id);
            return (
              <div
                key={a.id}
                className="dropdown-option"
                role="checkbox"
                aria-checked={checked}
                onClick={() => toggleItem(a.id)}
              >
                <span className={classNames('dropdown-check', { checked })}>
                  {checked ? (
                    <FontAwesomeIcon icon={faCheck} fixedWidth />
                  ) : null}
                </span>
                {a.name}
              </div>
            );
          })}
        </div>
      </FilterDropdown>
    </div>
  );
};

export default BillingAccountPill;
